<!-- Schedule.vue - CLEAN MODERN DESIGN -->
<template>
  <main class="flex-grow container mx-auto p-4 sm:p-6">
    <div class="text-center">
      <!-- Week Navigation -->
      <div class="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
        <button
            @click="previousWeek"
            class="group bg-slate-800 hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 text-white font-semibold py-2.5 px-5 rounded-xl shadow-md hover:shadow-lg transform hover:-translate-x-0.5 transition-all duration-200"
        >
          <span class="flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
            Previous
          </span>
        </button>
        <div class="text-center">
          <h3 class="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white">
            Week of {{ formatDate(currentWeekStart) }}
          </h3>
          <p class="text-base text-slate-500 dark:text-slate-400 mt-1">Week {{ weekNumber }}</p>
        </div>
        <button
            @click="nextWeek"
            class="group bg-slate-800 hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 text-white font-semibold py-2.5 px-5 rounded-xl shadow-md hover:shadow-lg transform hover:translate-x-0.5 transition-all duration-200"
        >
          <span class="flex items-center gap-2">
            Next
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </span>
        </button>
      </div>

      <!-- Day Navigation -->
      <div class="flex flex-wrap justify-center mb-8 gap-2">
        <button
            v-for="(day, index) in daysOfWeek"
            :key="index"
            @click="currentDayIndex = index"
            :class="[
            currentDayIndex === index
              ? 'bg-indigo-600 text-white shadow-md'
              : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700',
            'font-semibold py-2 px-5 rounded-lg transition-all duration-200 hover:shadow-md'
          ]"
        >
          {{ day }}
        </button>
      </div>

      <!-- Schedule Table -->
      <div class="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm shadow-xl rounded-2xl p-6 sm:p-8 border border-slate-200/50 dark:border-slate-700/50">
        <h4 class="text-2xl sm:text-3xl font-bold mb-6 text-slate-900 dark:text-white">
          {{ daysOfWeek[currentDayIndex] }}'s Schedule
        </h4>

        <div v-if="todaySchedulesWithBreaks.length" class="overflow-x-auto">
          <table class="min-w-full text-slate-800 dark:text-slate-100">
            <thead>
            <tr class="bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
              <th class="px-3 py-2.5 text-left text-sm font-semibold">Time</th>
              <th class="px-3 py-2.5 text-left text-sm font-semibold">Class</th>
              <th class="px-3 py-2.5 text-left text-sm font-semibold">Room</th>
            </tr>
            </thead>
            <tbody>
            <tr
                v-for="schedule in todaySchedulesWithBreaks"
                :key="schedule.section_id || schedule.start_time"
                :class="[
                  'border-b border-slate-200 dark:border-slate-700',
                  schedule.isBreak
                    ? 'bg-slate-50 dark:bg-slate-900'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                ]"
            >
              <td class="px-3 py-3 text-sm" :class="schedule.isBreak ? 'text-slate-500 dark:text-slate-400' : ''">
                {{ formatTime(schedule.start_time) }} - {{ formatTime(schedule.end_time) }}
              </td>
              <td class="px-3 py-3 text-sm font-medium">
                <span v-if="schedule.isBreak" class="text-slate-500 dark:text-slate-400 italic">
                  {{ schedule.duration }} minute break
                </span>
                <span v-else>{{ schedule.course_title }}</span>
              </td>
              <td class="px-3 py-3 text-sm">
                <span v-if="!schedule.isBreak">{{ schedule.room_name }}</span>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="text-center py-16">
          <svg class="w-16 h-16 mx-auto mb-3 text-slate-300 dark:text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p class="text-slate-500 dark:text-slate-400">No schedule for this day</p>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import axios from 'axios';
import { consumeTokenFromUrl, getActiveAuthToken, redirectToLogin as beginLogin } from '@/config/auth';
import { backendUrl } from '@/config/backend';

export default {
  data() {
    return {
      token: null,
      username: '',
      currentDayIndex: 0,
      currentWeekStart: new Date(),
      schedules: [],
      daysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      referenceStartDate: new Date('2023-11-11'),
    };
  },
  computed: {
    weekNumber() {
      const oneWeekInMs = 7 * 24 * 60 * 60 * 1000;
      const diffInMs = this.currentWeekStart - this.referenceStartDate;
      const diffInWeeks = Math.floor(diffInMs / oneWeekInMs);
      return (diffInWeeks % 2 === 0) ? 1 : 2;
    },

    todaySchedulesWithBreaks() {
      const schedulesWithBreaks = [];
      const currentDate = this.getDateByDay(this.currentWeekStart, this.currentDayIndex);
      const formattedDate = this.formatApiDate(currentDate);

      const todaySchedules = this.schedules
          .filter(schedule => schedule.start_time.split('T')[0] === formattedDate)
          .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

      for (let i = 0; i < todaySchedules.length; i++) {
        const currentClass = todaySchedules[i];
        schedulesWithBreaks.push(currentClass);

        if (i < todaySchedules.length - 1) {
          const nextClass = todaySchedules[i + 1];
          const currentClassEnd = new Date(currentClass.end_time);
          const nextClassStart = new Date(nextClass.start_time);
          const timeDiffMinutes = (nextClassStart - currentClassEnd) / (1000 * 60);

          if (timeDiffMinutes >= 15) {
            schedulesWithBreaks.push({
              isBreak: true,
              start_time: currentClass.end_time,
              end_time: nextClass.start_time,
              duration: Math.round(timeDiffMinutes)
            });
          }
        }
      }
      return schedulesWithBreaks;
    },
  },
  created() {
    this.extractToken();
    this.initializeWeekAndDay();
    this.fetchUsername();
    this.fetchSchedules();
  },
  methods: {
    initializeWeekAndDay() {
      this.currentWeekStart = this.getCurrentWeekStart();
      this.currentDayIndex = this.getCurrentDayIndex();
    },
    formatDate(date) {
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'America/New_York'  // Force Eastern timezone display
      });
    },
    extractToken() {
      const token = consumeTokenFromUrl() || getActiveAuthToken();

      if (token) {
        this.token = token;
      } else {
        beginLogin({ originalUrl: window.location.href });
      }
    },
    isTokenExpired(token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiry = payload.exp;
        const now = Math.floor(new Date().getTime() / 1000);
        return now > expiry;
      } catch (error) {
        console.error('Invalid token format:', error);
        return true;
      }
    },
    async fetchUsername() {
      if (!this.token || this.isTokenExpired(this.token)) {
        beginLogin({ originalUrl: window.location.href });
        return;
      }
      try {
        const response = await axios.get(backendUrl('/username'), {
          headers: { Authorization: `Bearer ${this.token}` },
        });
        this.username = response.data.username;
      } catch (error) {
        console.error('Error fetching username:', error);
        if (error.response && error.response.status === 401) {
          beginLogin({ originalUrl: window.location.href });
        }
      }
    },
    async fetchSchedules() {
      if (!this.token || this.isTokenExpired(this.token)) {
        beginLogin({ originalUrl: window.location.href });
        return;
      }
      try {
        const start_date = this.formatApiDate(this.currentWeekStart);
        const end_date = this.formatApiDate(new Date(this.currentWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000));

        const response = await axios.get(backendUrl('/schedule'), {
          params: { start_date, end_date },
          headers: { Authorization: `Bearer ${this.token}` },
        });

        this.schedules = response.data.value;
      } catch (error) {
        console.error('Error fetching schedule:', error);
        if (error.response && error.response.status === 401) {
          beginLogin({ originalUrl: window.location.href });
        }
      }
    },
    formatApiDate(date) {
      // Format date in Eastern timezone for API calls
      const easternFormatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      // en-CA gives YYYY-MM-DD format
      return easternFormatter.format(date);
    },
    formatTime(timeString) {
      // Parse ISO string and display in Eastern timezone
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'  // Force Eastern timezone display
      });
    },
    getDateByDay(weekStart, dayIndex) {
      if (!weekStart || !(weekStart instanceof Date)) {
        console.error('Invalid weekStart:', weekStart);
        return new Date();
      }
      // Add days to the week start date
      // weekStart is stored at 17:00 UTC (noon Eastern), so adding days keeps us at same time
      return new Date(weekStart.getTime() + dayIndex * 24 * 60 * 60 * 1000);
    },
    getCurrentDayIndex() {
      const now = new Date();

      // Get current date/time in Eastern timezone
      const easternFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        hour: '2-digit',
        hour12: false,
        weekday: 'short'
      });

      const parts = easternFormatter.formatToParts(now);
      const getPart = (type) => parts.find(p => p.type === type)?.value;

      const hourET = parseInt(getPart('hour'));
      const weekdayStr = getPart('weekday');

      const weekdayMap = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 };
      let dayOfWeek = weekdayMap[weekdayStr];

      // If after 4pm Eastern, advance to next day
      if (hourET >= 16) {
        if (dayOfWeek === 5) {
          dayOfWeek = 1; // Friday after 4pm -> Monday
        } else if (dayOfWeek === 6 || dayOfWeek === 0) {
          dayOfWeek = 1; // Weekend -> Monday
        } else {
          dayOfWeek += 1;
        }
      }

      if (dayOfWeek === 6 || dayOfWeek === 0) return 0;
      return dayOfWeek - 1;
    },
    getNextWeekStart(currentStart) {
      if (!currentStart || !(currentStart instanceof Date)) {
        console.error('Invalid currentStart:', currentStart);
        return new Date();
      }
      // Add 7 days to current week start
      return new Date(currentStart.getTime() + 7 * 24 * 60 * 60 * 1000);
    },
    getCurrentWeekStart() {
      const now = new Date();

      // Get current date/time components in Eastern timezone
      const easternFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        hour12: false,
        weekday: 'short'
      });

      const parts = easternFormatter.formatToParts(now);
      const getPart = (type) => parts.find(p => p.type === type)?.value;

      const yearET = parseInt(getPart('year'));
      const monthET = parseInt(getPart('month')) - 1; // 0-indexed for Date constructor
      const dayET = parseInt(getPart('day'));
      const hourET = parseInt(getPart('hour'));
      const weekdayStr = getPart('weekday');

      // Map weekday string to number (0=Sun, 1=Mon, ..., 6=Sat)
      const weekdayMap = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 };
      const dayOfWeekET = weekdayMap[weekdayStr];

      const isFridayAfter4pm = dayOfWeekET === 5 && hourET >= 16;
      const isWeekend = dayOfWeekET === 6 || dayOfWeekET === 0;

      let targetDay = dayET;

      if (isFridayAfter4pm || isWeekend) {
        const daysUntilNextMonday = (8 - dayOfWeekET) % 7 || 7;
        targetDay += daysUntilNextMonday;
      } else {
        const daysSinceMonday = (dayOfWeekET + 6) % 7;
        targetDay -= daysSinceMonday;
      }

      // Create date at noon UTC to avoid day boundary issues when converting timezones
      // 17:00 UTC = 12:00 noon Eastern (during EST) - safe middle of day
      const weekStartDate = new Date(Date.UTC(yearET, monthET, targetDay, 17, 0, 0, 0));

      return weekStartDate;
    },
    nextWeek() {
      // Add 7 days using timestamp math for consistency
      this.currentWeekStart = new Date(this.currentWeekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
      this.currentDayIndex = 0;
      this.fetchSchedules();
    },
    previousWeek() {
      // Subtract 7 days using timestamp math for consistency
      this.currentWeekStart = new Date(this.currentWeekStart.getTime() - 7 * 24 * 60 * 60 * 1000);
      this.currentDayIndex = 0;
      this.fetchSchedules();
    },
  },
};
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #6366f1;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: #4f46e5;
}

.dark::-webkit-scrollbar-thumb {
  background: #818cf8;
}

.dark::-webkit-scrollbar-thumb:hover {
  background: #6366f1;
}

table {
  width: 100%;
  border-spacing: 0;
}
</style>
